import { BuildEnvironment } from "@base/BuildEnvironment";
import { IVec3, Vec3 } from "@base/math/Vec3";
import { IdGen } from "@base/util/IdGen";
import { IMaterialSettings, ITexInfo } from "@biz/definitions/SunSailDefinitions";
import { getInitialMaterial } from "@biz/definitions/SunSailDefinitions.util";
import { GroundShape, IGroundData } from "@biz/items/ground/GroundDefinitions";

export class GroundData {
  static getPossibleAspect(groundData: IGroundData): number | undefined {
    // const aspect = groundData.texInfo.type === "image" ? 0.75 : undefined;
    if (groundData.texInfo.type === "pattern")
      return undefined;

    const image = groundData.texInfo.image;
    const imageSize = image?.type === "userFile" && image.imageSize;
    if (imageSize)
      return imageSize.w / imageSize.h;
    return undefined;
    // return undefined;
  }

  static getAspect(groundData: IGroundData): number | undefined {
    if (groundData.ignoreAspect)
      return undefined;
    return GroundData.getPossibleAspect(groundData);
  }

  static ensureAspect(old: IGroundData): IGroundData {
    const aspect = GroundData.getAspect(old);
    if (aspect) {
      const l = Vec3.len(old.corners[0], old.corners[1])
      const b = aspect ? l / aspect : Vec3.len(old.corners[1], old.corners[0])
      return {
        ...old,
        corners: GroundData.withSize(old.corners, l, b, 0)
      }
    }
    return old;
  }

  static withSize(corners: IVec3[], length: number, width: number, originPoint: number | "center" = "center"): IVec3[] {
    const ac = Vec3.to(corners[0], corners[corners.length - 1])
    const ab = Vec3.to(corners[0], corners[1])
    const acNormalized = Vec3.withY(Vec3.normalized(ac), 0);
    const abNormalized = Vec3.withY(Vec3.normalized(ab), 0);

    // originPoint = 0 as (number | "center");
    const origin = originPoint === "center" ? Vec3.center(corners) : corners[originPoint];
    const anchorWeights: Record<any, { x: number, y: number }> = {
      center: { x: 0.5, y: 0.5 },
      0: { x: 1, y: 1 },
      1: { x: 0, y: 1 },
      2: { x: 0, y: 0 },
      3: { x: 1, y: 0 },
    };
    const weights = anchorWeights[originPoint];

    const newCorners: IVec3[] = [
      Vec3.addScaled(Vec3.addScaled(origin, acNormalized, width * (weights.y - 1)), abNormalized, length * (weights.x - 1)),
      Vec3.addScaled(Vec3.addScaled(origin, acNormalized, width * (weights.y - 1)), abNormalized, length * weights.x),
      Vec3.addScaled(Vec3.addScaled(origin, acNormalized, width * weights.y), abNormalized, length * weights.x),
      Vec3.addScaled(Vec3.addScaled(origin, acNormalized, width * weights.y), abNormalized, length * (weights.x - 1)),
    ]
    return newCorners;
  }

  static create(pos: IVec3, texInfo: ITexInfo, options?: { material?: IMaterialSettings | string; shape?: GroundShape }): IGroundData {
    const gd: IGroundData = {
      itemType: "ground",
      version: "2022",
      shape: options?.shape || "rectangle",
      id: IdGen.unsecure.get(),
      buildTime: BuildEnvironment.BuildTimeAsNumber,
      height: 0.05,
      corners: GroundData.createCorners(pos, true),
      texInfo,
      material: options?.material || getInitialMaterial(texInfo.image)
    }
    return this.ensureAspect(gd);
  }

  static createCorners(pos: IVec3, centered: boolean): IVec3[] {
    const w = 5;
    const h = 5;

    const ox = centered ? -w * 0.5 : 0;
    const oz = centered ? -h * 0.5 : 0;

    return [
      Vec3.add(pos, Vec3.from(ox, 0, oz)),
      Vec3.add(pos, Vec3.from(ox + w, 0, oz)),
      Vec3.add(pos, Vec3.from(ox + w, 0, oz + h)),
      Vec3.add(pos, Vec3.from(ox, 0, oz + h)),
    ]
  }
}